import { useEffect, useRef, useState } from 'react';
import { Select } from '@gupshup-frontend/gupshup-ts-components';
import { getConsentCategories } from '../../api/gsIntg';
import ConsentAccessSetup from './ConsentAccessSetup';

const FIELD_ID = 'br-consent-input';
const FIELD_TEST_ID = 'bloomreach-message-consent';
const LABEL_ID = 'br-consent-label';
const HELP_ID = 'br-consent-help';
const NONE_LABEL = 'None (general consent)';
const LOADING_LABEL = 'Loading consent categories…';
// MUI draws an empty-string value as blank, so inside the Select None and Loading carry values of their own.
// onChange still gets '' for None.
const NONE_VALUE = '__general_consent__';
const LOADING_VALUE = '__loading__';
const LOADING_ITEMS = [{ value: LOADING_VALUE, label: LOADING_LABEL }];
const HINT_FAILED = "Couldn't load your project's consent categories. Type the category ID instead.";
const HINT_EMPTY =
  'This Bloomreach project has no consent categories. Add one in Bloomreach under Project settings › Privacy management › Consents, or leave this empty if your project allows general consent.';
const HINT_TYPED =
  "The category's ID in your Bloomreach project, under Project settings › Privacy management › Consents. Leave it empty for general consent.";
const PROMPT_NO_ACCESS = "This number has no Bloomreach API access yet, so the categories can't load.";
const SAVED = 'Saved. Bloomreach access is set up for this number.';

// The id is the value Bloomreach wants, so it stays visible whenever the name differs from it.
const optionLabel = ({ id, name }) => (name && name !== id ? `${name} (${id})` : id);

// None, the project's categories, and a saved category the project no longer has: it stays listed and selected, so it
// is never dropped without a pick.
const selectItems = (categories, current) => {
  const missing = !!current && !categories.some((category) => category.id === current);
  return [
    { value: NONE_VALUE, label: NONE_LABEL },
    ...categories.map((category) => ({ value: category.id, label: optionLabel(category) })),
    ...(missing ? [{ value: current, label: `${current} (not in this project)` }] : []),
  ];
};

// The message's Bloomreach consent category. Same card as Send to, so it takes its styles, and Message/index.js
// keeps both out of the double-brace watch by that class: the Select's hidden input sits inside it too. With a
// sending number it is the UI kit's Select over the project's own categories, read through gs-intg, with no hint of
// its own. Without one, or when that call fails or comes back empty, it is a text box: text being typed stays local
// until blur or Enter, and the ID is checked by Bloomreach on Done. Empty means general consent.
// The text box says why it is one. A project with no categories (a 200 with an empty list) has its own hint, apart
// from a failed load (any error but a 404). A 404 from gs-intg means the org holds no Bloomreach credentials for the
// number: the card then offers the setup form (ConsentAccessSetup) in place of the hint, inside this same .br-sendto
// root so the double-brace watch skips its inputs too. After a save, and after a 409 from it, the categories are asked
// for again: a save closes the form at once, a 409 keeps it until the new answer says whether it is still a 404.
// A save also writes the number's entry on the org, so onCredentialsSaved (optional) tells the parent to reload its copy
// of the org: the project id of a message saved from here on comes from that entry. A 409 calls it too: the number was
// set up meanwhile, maybe by someone else. The form calls it, not this component, so it runs even when the form is gone
// by the time the answer lands (a number change, a tab switch).
const ConsentControl = ({ orgId, number, value, onChange, onCredentialsSaved }) => {
  const [text, setText] = useState(value);
  // The last reply, tagged with the request it answers. A reply to another orgId or number counts as no reply yet.
  const [reply, setReply] = useState(null);
  // Bumped by a save, to ask for the categories once more.
  const [reloads, setReloads] = useState(0);
  const [setupOpen, setSetupOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const setUpButton = useRef(null);
  const restoreFocus = useRef(false);

  // Follows the committed value (a restore).
  useEffect(() => {
    setText(value);
  }, [value]);

  // A new number or org starts clean: the form closes (dropping what was typed) and the saved line goes.
  useEffect(() => {
    setSetupOpen(false);
    setSaved(false);
  }, [orgId, number]);

  // Cancel removes the button that had focus; hand focus back to the one that opened the form.
  useEffect(() => {
    if (setupOpen || !restoreFocus.current) return;
    restoreFocus.current = false;
    setUpButton.current?.focus();
  }, [setupOpen]);

  // ponytail: no cache, so every mount asks again; keep the lists in Message if that ever matters.
  useEffect(() => {
    if (!number) return undefined;
    let stale = false;
    (async () => {
      let list = [];
      let missing = false;
      let failed = false;
      try {
        list = await getConsentCategories(orgId, number);
      } catch (error) {
        // An error reads as an empty list: the text box takes over. A 404 says the number has no credentials yet;
        // any other error is a failed load, which is not the same as a project with no categories.
        missing = error?.response?.status === 404;
        failed = !missing;
      }
      if (!stale) setReply({ orgId, number, list, missing, failed });
    })();
    return () => {
      stale = true;
    };
  }, [orgId, number, reloads]);

  const answered = !!reply && reply.orgId === orgId && reply.number === number;
  const loading = !!number && !answered;
  const categories = answered ? reply.list : [];
  const picking = categories.length > 0;
  const noAccess = answered && reply.missing;
  const failed = answered && reply.failed;

  const handleSaved = () => {
    setSetupOpen(false);
    setSaved(true);
    // No reply yet puts the control back in its loading state until the new answer lands.
    setReply(null);
    setReloads((count) => count + 1);
  };

  // The setup answered 409: the number already has credentials. Ask again, but leave the reply as it is, so the form keeps
  // its message and values until the answer lands: a 404 again leaves it open, anything else closes it.
  const handleConflict = () => setReloads((count) => count + 1);

  const handleCancel = () => {
    restoreFocus.current = true;
    setSetupOpen(false);
  };

  const commit = () => {
    const next = text.trim();
    setText(next);
    if (next !== value) onChange(next);
  };

  let control;
  if (loading || picking) {
    const current = value.trim();
    control = (
      <Select
        id={FIELD_ID}
        labelId={LABEL_ID}
        // The card's own label names the field; the kit would draw a second one above the control.
        label=""
        size="small"
        width="100%"
        // Bloomreach's iframe is ~620px tall: a taller menu slides up over the field.
        maxHeightOfDropdown="240px"
        disabled={loading}
        items={loading ? LOADING_ITEMS : selectItems(categories, current)}
        value={loading ? LOADING_VALUE : current || NONE_VALUE}
        handleChange={(selected) => onChange(selected === NONE_VALUE ? '' : selected)}
        SelectDisplayProps={{ 'data-testid': FIELD_TEST_ID }}
      />
    );
  } else {
    control = (
      <input
        id={FIELD_ID}
        className="br-sendto__input"
        data-testid={FIELD_TEST_ID}
        aria-describedby={HELP_ID}
        type="text"
        spellCheck={false}
        autoComplete="off"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          commit();
        }}
      />
    );
  }

  // Answered with a text box and no failure: the project has no categories. A 404 never gets here (it shows the prompt).
  // The dropdown, loading or ready, has no hint.
  let hint = HINT_TYPED;
  if (loading || picking) hint = null;
  else if (failed) hint = HINT_FAILED;
  else if (number) hint = HINT_EMPTY;

  return (
    <div className="br-sendto">
      <label id={LABEL_ID} className="br-sendto__label" htmlFor={FIELD_ID}>
        Consent category
      </label>
      {control}
      <div id={HELP_ID} className="br-sendto__help">
        {noAccess ? (
          <p className="br-sendto__hint" data-testid="bloomreach-consent-setup-prompt">
            {PROMPT_NO_ACCESS}
            {!setupOpen && (
              <>
                {' '}
                <button
                  ref={setUpButton}
                  type="button"
                  className="br-sendto__reset"
                  data-testid="bloomreach-consent-setup-open"
                  onClick={() => setSetupOpen(true)}
                >
                  Set up
                </button>
              </>
            )}
          </p>
        ) : (
          hint && <p className="br-sendto__hint">{hint}</p>
        )}
        <p className="br-consent-setup__saved" aria-live="polite" data-testid="bloomreach-consent-setup-saved">
          {saved && SAVED}
        </p>
      </div>
      {noAccess && setupOpen && (
        <ConsentAccessSetup
          orgId={orgId}
          number={number}
          onSaved={handleSaved}
          onCredentialsSaved={onCredentialsSaved}
          onConflict={handleConflict}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default ConsentControl;
